import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  ArrowLeft,
  Save,
  X,
  ChefHat,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import { demoRecipes } from '../seedData';
import { ImageUpload } from '../components/ImageUpload';
import { categories, getCategoryLabel } from '../lib/categories';

const difficulties = [
  { id: 'einfach', name: 'Einfach' },
  { id: 'mittel', name: 'Mittel' },
  { id: 'schwer', name: 'Schwer' },
];

const emptyRecipe = {
  title: '',
  description: '',
  imageUrl: '',
  category: '',
  difficulty: '',
  prepTime: '',
  servings: '',
  ingredients: [''],
  instructions: [''],
  tips: ''
};

export const Admin = () => {
  const { user, userProfile, isAdmin, loading: authLoading } = useAuth();
  
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [formData, setFormData] = useState(emptyRecipe);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const seedDemoRecipes = async () => {
    setSeeding(true);
    try {
      for (const recipe of demoRecipes) {
        await addDoc(collection(db, 'recipes'), recipe);
      }
      await fetchRecipes();
      toast.success('Demo-Rezepte hinzugefügt!');
    } catch (error) {
      console.error('Error seeding:', error);
      toast.error('Fehler beim Hinzufügen der Demo-Rezepte');
    } finally {
      setSeeding(false);
    }
  };

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'recipes'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const recipesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecipes(recipesData);
    } catch (error) {
      console.error('Error fetching recipes:', error);
      // Versuche ohne orderBy falls Index fehlt
      try {
        const snapshot = await getDocs(collection(db, 'recipes'));
        const recipesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRecipes(recipesData);
      } catch (err) {
        toast.error('Fehler beim Laden der Rezepte');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchRecipes();
    } else if (!authLoading && userProfile !== null) {
      setLoading(false);
    }
  }, [isAdmin, authLoading, userProfile]);

  const handleEdit = (recipe) => {
    setSelectedRecipe(recipe);
    setFormData({
      ...recipe,
      ingredients: recipe.ingredients?.length ? recipe.ingredients : [''],
      instructions: recipe.instructions?.length ? recipe.instructions : ['']
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedRecipe(null);
    setFormData(emptyRecipe);
    setDialogOpen(true);
  };

  const handleDelete = (recipe) => {
    setSelectedRecipe(recipe);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteDoc(doc(db, 'recipes', selectedRecipe.id));
      setRecipes(recipes.filter(r => r.id !== selectedRecipe.id));
      toast.success('Rezept gelöscht!');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRecipe(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const recipeData = {
        ...formData,
        ingredients: formData.ingredients.filter(i => i.trim()),
        instructions: formData.instructions.filter(i => i.trim()),
        prepTime: parseInt(formData.prepTime) || 0,
        servings: parseInt(formData.servings) || 0,
        updatedAt: new Date().toISOString()
      };

      if (selectedRecipe) {
        // Update existing
        await updateDoc(doc(db, 'recipes', selectedRecipe.id), recipeData);
        setRecipes(recipes.map(r => r.id === selectedRecipe.id ? { ...r, ...recipeData } : r));
        toast.success('Rezept aktualisiert!');
      } else {
        // Create new
        recipeData.createdAt = new Date().toISOString();
        const docRef = await addDoc(collection(db, 'recipes'), recipeData);
        setRecipes([{ id: docRef.id, ...recipeData }, ...recipes]);
        toast.success('Rezept erstellt!');
      }
      
      setDialogOpen(false);
      setSelectedRecipe(null);
      setFormData(emptyRecipe);
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateIngredient = (index, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = value;
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const addIngredient = () => {
    setFormData({ ...formData, ingredients: [...formData.ingredients, ''] });
  };

  const removeIngredient = (index) => {
    const newIngredients = formData.ingredients.filter((_, i) => i !== index);
    setFormData({ ...formData, ingredients: newIngredients.length ? newIngredients : [''] });
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData({ ...formData, instructions: newInstructions });
  };

  const addInstruction = () => {
    setFormData({ ...formData, instructions: [...formData.instructions, ''] });
  };

  const removeInstruction = (index) => {
    const newInstructions = formData.instructions.filter((_, i) => i !== index);
    setFormData({ ...formData, instructions: newInstructions.length ? newInstructions : [''] });
  };

  // Show loading while checking auth
  if (authLoading || (user && userProfile === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-olive">Lade...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ChefHat className="w-10 h-10 text-terracotta" />
          </div>
          <h2 className="font-display text-2xl font-bold text-navy mb-2">
            Bitte anmelden
          </h2>
          <p className="text-olive mb-6">Du musst angemeldet sein, um diese Seite zu sehen.</p>
          <Link to="/login">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Zur Anmeldung
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ChefHat className="w-10 h-10 text-terracotta" />
          </div>
          <h2 className="font-display text-2xl font-bold text-navy mb-2">
            Kein Zugriff
          </h2>
          <p className="text-olive mb-6">Du benötigst Admin-Rechte für diese Seite.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/setup-admin">
              <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
                Admin werden
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-full px-6">
                Zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Link 
          to="/profil" 
          className="inline-flex items-center gap-2 text-navy hover:text-terracotta mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Zurück zum Profil</span>
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-2">
              Rezepte verwalten
            </h1>
            <p className="text-olive">{recipes.length} Rezepte insgesamt</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={handleNew}
                className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6 gap-2"
                data-testid="add-recipe-btn"
              >
                <Plus className="w-5 h-5" />
                Neues Rezept
              </Button>
            </DialogTrigger>
            
          {recipes.length === 0 && (
            <Button
              onClick={seedDemoRecipes}
              disabled={seeding}
              variant="outline"
              className="rounded-full px-6 gap-2 ml-2"
              data-testid="seed-recipes-btn"
            >
              <Database className="w-5 h-5" />
              {seeding ? 'Lade...' : 'Demo-Rezepte laden'}
            </Button>
          )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {selectedRecipe ? 'Rezept bearbeiten' : 'Neues Rezept'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label>Titel *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Rezeptname"
                      required
                      className="mt-1"
                      data-testid="recipe-title-input"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Beschreibung</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Kurze Beschreibung des Rezepts"
                      className="mt-1"
                      rows={2}
                      data-testid="recipe-description-input"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Bild</Label>
                    <ImageUpload
                      value={formData.imageUrl}
                      onChange={(url) => setFormData({ ...formData, imageUrl: url })}
                      className="mt-1"
                    />
                    <div className="mt-2">
                      <Label className="text-xs text-olive">Oder URL eingeben:</Label>
                      <Input
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        placeholder="https://..."
                        className="mt-1"
                        data-testid="recipe-image-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Kategorie *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger className="mt-1" data-testid="recipe-category-select">
                        <SelectValue placeholder="Wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Schwierigkeit</Label>
                    <Select 
                      value={formData.difficulty} 
                      onValueChange={(v) => setFormData({ ...formData, difficulty: v })}
                    >
                      <SelectTrigger className="mt-1" data-testid="recipe-difficulty-select">
                        <SelectValue placeholder="Wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {difficulties.map(diff => (
                          <SelectItem key={diff.id} value={diff.id}>{diff.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Zubereitungszeit (Min)</Label>
                    <Input
                      type="number"
                      value={formData.prepTime}
                      onChange={(e) => setFormData({ ...formData, prepTime: e.target.value })}
                      placeholder="30"
                      className="mt-1"
                      data-testid="recipe-time-input"
                    />
                  </div>

                  <div>
                    <Label>Portionen</Label>
                    <Input
                      type="number"
                      value={formData.servings}
                      onChange={(e) => setFormData({ ...formData, servings: e.target.value })}
                      placeholder="4"
                      className="mt-1"
                      data-testid="recipe-servings-input"
                    />
                  </div>
                </div>

                {/* Ingredients */}
                <div>
                  <Label>Zutaten *</Label>
                  <div className="space-y-2 mt-1">
                    {formData.ingredients.map((ingredient, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={ingredient}
                          onChange={(e) => updateIngredient(index, e.target.value)}
                          placeholder={`Zutat ${index + 1}`}
                          data-testid={`ingredient-input-${index}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeIngredient(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addIngredient}
                      className="gap-1"
                    >
                      <Plus className="w-4 h-4" /> Zutat hinzufügen
                    </Button>
                  </div>
                </div>

                {/* Instructions */}
                <div>
                  <Label>Zubereitung *</Label>
                  <div className="space-y-2 mt-1">
                    {formData.instructions.map((instruction, index) => (
                      <div key={index} className="flex gap-2">
                        <span className="flex-shrink-0 w-8 h-10 bg-terracotta/10 rounded flex items-center justify-center text-terracotta font-bold text-sm">
                          {index + 1}
                        </span>
                        <Textarea
                          value={instruction}
                          onChange={(e) => updateInstruction(index, e.target.value)}
                          placeholder={`Schritt ${index + 1}`}
                          rows={2}
                          className="flex-1"
                          data-testid={`instruction-input-${index}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeInstruction(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addInstruction}
                      className="gap-1"
                    >
                      <Plus className="w-4 h-4" /> Schritt hinzufügen
                    </Button>
                  </div>
                </div>

                {/* Tips */}
                <div>
                  <Label>Tipps (optional)</Label>
                  <Textarea
                    value={formData.tips}
                    onChange={(e) => setFormData({ ...formData, tips: e.target.value })}
                    placeholder="Tipps und Tricks..."
                    className="mt-1"
                    rows={2}
                    data-testid="recipe-tips-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 rounded-xl"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white rounded-xl gap-2"
                    disabled={saving || !formData.title || !formData.category}
                    data-testid="save-recipe-btn"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Recipe List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl h-24 animate-pulse" />
            ))}
          </div>
        ) : recipes.length > 0 ? (
          <div className="space-y-4">
            {recipes.map((recipe, index) => (
              <motion.div
                key={recipe.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4"
              >
                <img
                  src={recipe.imageUrl || 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg'}
                  alt={recipe.title}
                  className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-navy truncate">{recipe.title}</h3>
                  <p className="text-sm text-olive">
                    {getCategoryLabel(recipe.category)}
                    {recipe.prepTime && ` • ${recipe.prepTime} Min`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(recipe)}
                    className="text-navy hover:text-terracotta"
                    data-testid={`edit-recipe-${recipe.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(recipe)}
                    className="text-red-500 hover:text-red-600"
                    data-testid={`delete-recipe-${recipe.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-bone-alt rounded-full flex items-center justify-center mx-auto mb-6">
              <ChefHat className="w-10 h-10 text-olive" />
            </div>
            <h3 className="font-display text-xl font-semibold text-navy mb-2">
              Keine Rezepte
            </h3>
            <p className="text-olive mb-6">
              Erstelle dein erstes Rezept!
            </p>
            <Button 
              onClick={handleNew}
              className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6 gap-2"
            >
              <Plus className="w-5 h-5" />
              Neues Rezept
            </Button>
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Rezept löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchtest du "{selectedRecipe?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDelete}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-btn"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
